// File: src/components/AffinitySoftGateSheet.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoPeopleOutline } from 'react-icons/io5';

/**
 * ✅ Generic Audience Soft Gate
 * - Shows when an event is restricted (affinity, gender gate, orientation, lane caps, etc.)
 * - DOES NOT disclose which affinity or which restriction
 * - Hard gates (age/gender/orientation denies) should remain separate and authoritative
 *
 * Backwards compatible props (so you don’t have to refactor):
 * - orientation, purpose, genderGate are accepted but intentionally NOT used for copy
 */
export default function AffinitySoftGateSheet({
  // kept for backward compatibility — intentionally not used
  orientation = 'open',
  purpose = 'dating',
  genderGate = 'all',

  // used (but we still keep copy generic)
  affinity = false,

  // optional: pass true if you want to force generic restricted language even when affinity=false
  restricted = true,

  // handlers
  onConfirm,
  onCancel,
}) {
  const [progress, setProgress] = useState(0);
  const ready = progress >= 100;

  const overlayLockRef = useRef({ didLock: false, y: 0, prevTheme: '' });

  // ✅ Freeze background scroll + notch color while this sheet is mounted
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.content;

    const alreadyLocked =
      document.body.classList.contains('overlay-active') ||
      document.body.style.position === 'fixed';

    overlayLockRef.current.didLock = !alreadyLocked;

    // notch/status bar
    overlayLockRef.current.prevTheme = prevTheme || '#ffffff';
    if (meta) meta.content = '#999998';

    // Freeze scroll at current position (only if nothing else already locked it)
    if (!alreadyLocked) {
      const y = window.scrollY || window.pageYOffset || 0;
      overlayLockRef.current.y = y;

      document.body.style.top = `-${y}px`;
      document.body.classList.add('overlay-active');
    }

    return () => {
      // restore notch
      if (meta) meta.content = overlayLockRef.current.prevTheme || '#ffffff';

      // restore scroll only if we locked it
      if (overlayLockRef.current.didLock) {
        const y = overlayLockRef.current.y || 0;

        document.body.classList.remove('overlay-active');
        document.body.style.top = '';
        window.scrollTo(0, y);
      }
    };
  }, []);

  useEffect(() => {
    const duration = 3500; // a little faster than 5s so it doesn’t feel stuck
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(100, Math.round((elapsed / duration) * 100));
      setProgress(p);
      if (p >= 100) clearInterval(id);
    }, 50);
    return () => clearInterval(id);
  }, []);

  const headline = useMemo(() => {
    // If affinity is on, we can say “designated identity group” without saying WHICH one.
    if (affinity) return 'This event is for a designated identity group';

    // If it’s restricted but not affinity, still be generic.
    if (restricted) return 'This event is limited to a specific audience';

    // If someone accidentally opens this when there is no restriction, still safe copy.
    return 'Before you continue';
  }, [affinity, restricted]);

  const lines = useMemo(() => {
    // Never specify the exact gate (gender/orientation/etc.)
    if (affinity) {
      return [
        'This event is configured for a designated identity group.',
        'Please continue only if you belong to the intended audience.',
      ];
    }
    if (restricted) {
      return [
        'This event is configured for a specific audience.',
        'Please continue only if you belong to the intended audience.',
      ];
    }
    return ['Please confirm you understand before continuing.'];
  }, [affinity, restricted]);

  return (
    <div
      onClick={() => {
        if (ready) onCancel?.();
      }}
      style={S.backdrop}
      role="presentation"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Event audience"
        style={S.sheet}
      >
        <div style={S.header}>
          <div style={S.headerTitle}>Event Audience</div>
        </div>

        <div style={S.body}>
          <div style={S.illustration} aria-hidden>
            <IoPeopleOutline style={{ fontSize: 56, color: '#0b0b0b' }} />
          </div>

          <div style={S.section}>
            <div style={S.headline}>{headline}</div>
            {lines.map((txt, idx) => (
              <div key={idx} style={S.subheading}>
                {txt}
              </div>
            ))}
          </div>
        </div>

        <div style={S.footer}>
          {!ready ? (
            <div style={S.progressRow}>
              <div
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                style={S.progressTrack}
              >
                <div style={{ ...S.progressFill, width: `${progress}%` }} />
              </div>
            </div>
          ) : (
            <div style={S.footerRow}>
              <button type="button" onClick={onCancel} style={S.secondaryBtn}>
                Cancel
              </button>
              <button type="button" onClick={() => onConfirm?.()} style={S.primaryBtn}>
                Continue
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(16px); opacity:0 } to { transform: translateY(0); opacity:1 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
        button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
      `}</style>
    </div>
  );
}

/* ===============================
         STYLE TOKENS
=============================== */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 520,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .25s ease-out',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 16px',
    borderBottom: '1px solid #eef0f2',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },
  body: {
    maxHeight: 'min(64vh, 560px)',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '8px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  section: { background: '#fff' },
  illustration: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 240,
    marginTop: 4,
    marginBottom: 6,
    border: '1px dashed #eef0f2',
    borderRadius: 12,
    background: '#fafafa',
  },
  headline: {
    fontSize: 16,
    fontWeight: 900,
    color: '#0b0b0b',
    margin: '2px 2px 6px',
    letterSpacing: '-0.01em',
  },
  subheading: {
    fontSize: 13.5,
    color: '#374151',
    lineHeight: 1.55,
    margin: '0 2px 6px',
  },
  footer: { padding: 12, borderTop: '1px solid #eef0f2' },
  footerRow: { display: 'flex', gap: 8 },
  progressRow: { display: 'flex', alignItems: 'center', gap: 8 },
  progressTrack: {
    width: '100%',
    height: 12,
    background: '#eef0f2',
    borderRadius: 999,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  progressFill: {
    height: '100%',
    background: '#3579F6',
    borderRadius: 999,
    transition: 'width .2s ease',
  },
  secondaryBtn: {
    flex: 1,
    background: '#ffffff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
  primaryBtn: {
    flex: 1,
    background: '#000',
    color: '#fff',
    border: '1px solid #000',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};
